import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from 'firebase/auth';
import { UserPlan, onAuthStateChange, subscribeToUserPlan, signInWithGoogle, signOut as firebaseSignOut, getOfflineUserPlan, setOfflineUserPlan, clearOfflineUserPlan, forceSyncCredits, fixUserPlan, handleAuthRedirectResult, addCreditsRestToPlan } from '../services/firebase';

interface AuthContextType {
  user: User | null;
  userPlan: UserPlan | null;
  isLoading: boolean;
  planLoading: boolean;
  isPaidUser: boolean;
  isAnonymous: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  refreshUserPlan: () => Promise<void>;
  updateUserPlan: (newPlan: UserPlan) => void;
  forceSyncCredits: () => Promise<void>;
  fixUserPlan: (planType: 'standard' | 'pro' | 'pro_max') => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  userPlan: null,
  isLoading: true,
  planLoading: false,
  isPaidUser: false,
  isAnonymous: false,
  signIn: async () => {},
  signOut: async () => {},
  refreshUserPlan: async () => {},
  updateUserPlan: () => {},
  forceSyncCredits: async () => {},
  fixUserPlan: async () => {},
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  
  // キャッシュ優先: 画面遷移直後は localStorage のプランを先に出して 0 点滅を防ぐ
  const [userPlan, setUserPlan] = useState<UserPlan | null>(null);
  
  const [isLoading, setIsLoading] = useState(true);
  const [planLoading, setPlanLoading] = useState(false);
  
  const [planLoaded, setPlanLoaded] = useState(false);

  const applyPlan = (plan: UserPlan | null, uid?: string) => {
    if (!plan) {
      setUserPlan(null);
      return;
    }
    const normalized = addCreditsRestToPlan(plan);
    setUserPlan(normalized);
    if (uid) {
      setOfflineUserPlan(uid, normalized);
    }
  };

  useEffect(() => {
    try {
      localStorage.removeItem('savedUserData');
    } catch {
      // ignore
    }

    const handleRedirect = async () => {
      try {
        const redirectUser = await handleAuthRedirectResult();
        if (redirectUser) {
          setUser(redirectUser);
        }
      } catch (error) {
        console.error('🔍 AuthContext - リダイレクト結果処理エラー:', error);
      }
    };

    handleRedirect();

    const unsubscribeAuth = onAuthStateChange((nextUser) => {
      console.log('🔍 AuthContext - 認証状態変更:', nextUser ? `ユーザー: ${nextUser.uid}` : 'ログアウト');
      setUser(nextUser);
      setIsLoading(false);

      if (!nextUser) {
        setUserPlan(null);
        setPlanLoaded(false);
        setPlanLoading(false);
      } else {
        // 認証復帰直後にキャッシュを先出し（DB待ちで 0 にならない）
        const cached = getOfflineUserPlan(nextUser.uid);
        if (cached) {
          applyPlan(cached, nextUser.uid);
          console.log('🔍 AuthContext - オフラインキャッシュを先出し', cached);
        }
      }
    });

    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    if (!user) {
      setPlanLoading(false);
      setPlanLoaded(false);
      return;
    }

    let cancelled = false;
    setPlanLoading(true);

    // 1) キャッシュ即時反映
    const cached = getOfflineUserPlan(user.uid);
    if (cached) {
      applyPlan(cached, user.uid);
    }

    // 2) Firestore から最新取得 + 購読
    const bootstrap = async () => {
      try {
        const { getUserPlan } = await import('../services/firebase');
        const latestPlan = await getUserPlan(user.uid);
        if (cancelled) return;

        if (latestPlan) {
          applyPlan(latestPlan, user.uid);
        } else if (!cached) {
          applyPlan({
            plan: 'free',
            creditsUsed: 0,
            creditsRest: 3,
            purchaseDate: new Date().toISOString(),
            lastUpdated: new Date().toISOString(),
          }, user.uid);
        }
      } catch (error) {
        console.error('❌ AuthContext - プラン取得エラー（キャッシュ維持）:', error);
        // キャッシュがある場合は上書きしない
        if (!cancelled && !cached && !userPlan) {
          applyPlan({
            plan: 'free',
            creditsUsed: 0,
            creditsRest: 3,
          }, user.uid);
        }
      } finally {
        if (!cancelled) {
          setPlanLoading(false);
        }
      }
    };

    bootstrap();

    const unsubscribePlan = subscribeToUserPlan(user.uid, (plan) => {
      if (cancelled) return;

      // タイムアウト等の仮 free(0) で、既存の有料/キャッシュを潰さない
      const looksLikeEmptyDefault =
        !!plan &&
        plan.plan === 'free' &&
        (plan.creditsUsed ?? 0) === 0 &&
        (plan.creditsRest === undefined || plan.creditsRest === 0);

      const cachedNow = getOfflineUserPlan(user.uid);
      const hasRicherCache =
        !!cachedNow &&
        (cachedNow.plan !== 'free' || (cachedNow.creditsRest ?? 0) > 0 || (cachedNow.creditsUsed ?? 0) > 0);

      if (looksLikeEmptyDefault && hasRicherCache) {
        console.warn('🔍 AuthContext - 空のデフォルトプランを無視しキャッシュを維持', {
          incoming: plan,
          cached: cachedNow,
        });
        applyPlan(cachedNow, user.uid);
        setPlanLoading(false);
        setPlanLoaded(true);
        return;
      }

      if (plan) {
        applyPlan(plan, user.uid);
      }
      setPlanLoading(false);
      setPlanLoaded(true);
    });

    return () => {
      cancelled = true;
      unsubscribePlan();
      setPlanLoading(false);
    };
  }, [user?.uid]);

  const isPaidUser = !!(userPlan && userPlan.plan !== 'free');
  const isAnonymous = user?.isAnonymous || false;

  const signIn = async () => {
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error('ログインエラー:', error);
      throw error;
    }
  };

  // 匿名ユーザー機能は廃止 - ログイン必須に変更

  const signOut = async () => {
    try {
      console.log('🔍 AuthContext - ログアウト開始');
      const uid = user?.uid;
      
      setUser(null);
      setUserPlan(null);
      setPlanLoaded(false);
      setPlanLoading(false);
      
      await firebaseSignOut();
      
      if (uid) {
        clearOfflineUserPlan(uid);
      }
      sessionStorage.clear();
      
      console.log('🔍 AuthContext - 完全クリーンアップ完了');
    } catch (error) {
      console.error('ログアウトエラー:', error);
      throw error;
    }
  };

  const refreshUserPlan = async () => {
    console.log('🔄 AuthContext - Refreshing user plan...');
    if (!user) {
      console.warn('No user logged in, cannot refresh plan');
      return;
    }
    
    try {
      const { getUserPlan } = await import('../services/firebase');
      const latestPlan = await getUserPlan(user.uid);
      console.log('🔍 AuthContext - Refreshed plan:', latestPlan);
      
      if (latestPlan) {
        applyPlan(latestPlan, user.uid);
        console.log('🔍 AuthContext - Plan refresh completed');
      }
    } catch (error) {
      console.error('❌ AuthContext - Plan refresh error:', error);
    }
  };

  // プラン情報を手動で更新する関数（決済完了後などに使用）
  const updateUserPlan = (newPlan: UserPlan) => {
    console.log('AuthContext - Manually updating user plan:', newPlan);
    applyPlan(newPlan, user?.uid);
  };

  // クレジット情報を強制的に同期する関数
  const forceSyncCreditsHandler = async () => {
    if (!user) {
      console.warn('No user logged in, cannot sync credits');
      return;
    }
    
    try {
      console.log('🔄 Force syncing credits from AuthContext...');
      const syncedPlan = await forceSyncCredits(user.uid);
      if (syncedPlan) {
        applyPlan(syncedPlan, user.uid);
        console.log('✅ Credits force synced successfully');
      } else {
        console.warn('⚠️ Force sync returned null - プラン情報が見つかりません');
      }
    } catch (error) {
      console.error('❌ Force sync failed:', error);
    }
  };

  // ユーザープランを手動で修正する関数（デバッグ用）
  const fixUserPlanHandler = async (planType: 'standard' | 'pro' | 'pro_max') => {
    if (!user) {
      console.warn('No user logged in, cannot fix plan');
      return;
    }
    
    try {
      console.log(`🔧 Fixing user plan to ${planType}...`);
      const fixedPlan = await fixUserPlan(user.uid, planType);
      if (fixedPlan) {
        console.log('✅ User plan fixed:', fixedPlan);
        setUserPlan(fixedPlan);
        setOfflineUserPlan(user.uid, fixedPlan);
      } else {
        console.error('❌ Failed to fix user plan');
      }
    } catch (error) {
      console.error('❌ Fix user plan failed:', error);
    }
  };

  const value = {
    user,
    userPlan,
    isLoading,
    planLoading,
    isPaidUser,
    isAnonymous,
    signIn,
    signOut,
    refreshUserPlan,
    updateUserPlan,
    forceSyncCredits: forceSyncCreditsHandler,
    fixUserPlan: fixUserPlanHandler,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
